import { useAuthStore } from '@/stores/auth'

const BASE_URL = import.meta.env.VITE_API_BASE_URL

export class ApiError extends Error {
  status: number
  detail?: string
  constructor(status: number, message: string, detail?: string) {
    super(message)
    this.status = status
    this.detail = detail
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  query?: Record<string, string | undefined>
  body?: unknown
}

function buildUrl(path: string, query?: Record<string, string | undefined>): string {
  const url = new URL(BASE_URL + path)
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== '') url.searchParams.set(key, value)
    }
  }
  return url.toString()
}

/**
 * Judge-Api (API Gateway HTTP API) への共通リクエスト関数。
 * Cognitoで取得したIDトークンを Authorization ヘッダに自動付与する。
 * (Judge-Api側は AWS::ApiGatewayV2::Authorizer(JWT) でこのトークンを検証する)
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const auth = useAuthStore()
  const token = auth.accessToken

  const res = await fetch(buildUrl(path, options.query), {
    method: options.method ?? 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  })

  if (!res.ok) {
    let message = `APIエラー (HTTP ${res.status})`
    let detail: string | undefined
    try {
      const errBody = await res.json()
      message = errBody.message ?? message
      detail = errBody.detail
    } catch {
      // レスポンスがJSONでない場合はそのまま
    }
    throw new ApiError(res.status, message, detail)
  }

  // 204 No Content 等、ボディが無いレスポンスに対応
  const text = await res.text()
  return text ? (JSON.parse(text) as T) : (undefined as T)
}
